<template>
  <div class="mcp">
    <el-alert :title="$t('machinesHub.controlHint')" type="info" :closable="false" show-icon class="mcp__hint"></el-alert>

    <!-- 主要动作：远程登录 + 电子锁 -->
    <div class="mcp__primaries">
      <el-button type="primary" icon="el-icon-monitor" :disabled="!online" @click="handleControl">{{ $t("machinesHub.remoteLogin") }}</el-button>
      <el-button type="primary" plain icon="el-icon-unlock" :disabled="!online" @click="openUnlock">{{ $t("machinesHub.electronicLock") }}</el-button>
    </div>

    <div class="mcp__grid">
      <!-- 以太网（开/关） -->
      <el-dropdown trigger="click" @command="onNetwork" :disabled="!online">
        <el-button icon="el-icon-connection" :disabled="!online" class="mcp__ddbtn">
          {{ $t("machinesHub.ethernet") }}<i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="on"><i class="el-icon-open"></i> {{ $t("machinesHub.networkOn") }}</el-dropdown-item>
          <el-dropdown-item command="off"><i class="el-icon-turn-off"></i> {{ $t("machinesHub.networkOff") }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <!-- 重启（合并 app/device） -->
      <el-dropdown trigger="click" @command="onRestart" :disabled="!online">
        <el-button type="danger" plain icon="el-icon-refresh-right" :disabled="!online" class="mcp__ddbtn">
          {{ $t("machinesHub.restart") }}<i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="2">{{ $t("machinesHub.restartApp") }}</el-dropdown-item>
          <el-dropdown-item command="1">{{ $t("machinesHub.restartDevice") }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <p v-if="!online" class="mcp__offline">{{ $t("machinesHub.offlineDisabled") }}</p>
  </div>
</template>

<script>
  // 设备远程控制面板：把原本埋在地图页设备抽屉里的功能集中、按机直达。
  // 逻辑与端点沿用 MachineMaps.vue（userEquipment/remote·restart·openNetWork·closeNetWork·electricLockDirect）。
  // 远程登录复用共享的 utils/remoteLogin（含取消与过期防护，避免与行内按钮实现漂移）。
  import { remoteLogin, cancelRemoteLogin } from "@/utils/remoteLogin";

  export default {
    name: "MachineControlPanel",
    props: {
      machine: { type: Object, default: () => ({}) }
    },
    beforeDestroy() {
      cancelRemoteLogin(this);
    },
    data() {
      return {
      };
    },
    computed: {
      online() {
        return Number(this.machine && this.machine.mqState) === 1;
      }
    },
    methods: {
      // ---- 网络 ----
      onNetwork(cmd) {
        const url = cmd === "on" ? this.$url.UserMachine.openNetWork : this.$url.UserMachine.closeNetWork;
        this.$api
          .request(url, this.$method.post, { equipmentNo: this.machine.equipmentNo })
          .then(res => {
            this.$notify({ title: this.$t("common.tip"), message: res.msg || this.$t("machinesHub.commandFailed"), type: res.code === 0 ? "success" : "warning" });
          })
          .catch(() => this.$notify({ title: this.$t("common.tip"), message: "request error", type: "error" }));
      },
      // ---- 重启 ----
      onRestart(type) {
        this.$confirm(type === "2" ? this.$t("user.MachineMaps.588934-5") : this.$t("user.MachineMaps.588934-6"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api
            .request(this.$url.UserMachine.reStartBoard, this.$method.post, { equipmentNo: this.machine.equipmentNo, type })
            .then(res => {
              this.$notify({ title: this.$t("common.tip"), message: res.msg || this.$t("machinesHub.commandFailed"), type: res.code === 0 ? "success" : "warning" });
            })
            .catch(() => this.$notify({ title: this.$t("common.tip"), message: "request error", type: "warning" }));
        });
      },
      // ---- 远程连接（复用共享工具，连接成功后新标签页打开远程桌面）----
      handleControl() {
        remoteLogin(this, this.machine);
      },
      // ---- 开锁 ----
      // 线上用的是「直接开锁」（ConfigUserMachine.vue 里的 electricLockDirect），
      // 不发短信/邮箱验证码。这里跟线上保持一致：确认一次就开，不再弹验证码框。
      openUnlock() {
        this.$confirm(this.$t("machinesHub.unlockConfirm"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            const loading = this.$loading({
              lock: true,
              text: this.$t("common.request"),
              spinner: "el-icon-loading",
              background: "rgba(0,0,0,0.7)"
            });
            this.$api
              .request(this.$url.UserMachine.electricLockDirect, this.$method.post, { equipmentNo: this.machine.equipmentNo })
              .then(res => {
                loading.close();
                this.$notify({
                  title: this.$t("common.tip"),
                  message: Number(res.code) === 0 ? this.$t("machinesHub.unlockSent") : res.msg || this.$t("machinesHub.commandFailed"),
                  type: Number(res.code) === 0 ? "success" : "warning"
                });
              })
              .catch(() => {
                loading.close();
                this.$notify({ title: this.$t("common.tip"), message: "request error", type: "warning" });
              });
          })
          .catch(() => {});
      }
    }
  };
</script>

<style scoped lang="scss">
  .mcp__hint {
    margin-bottom: 14px;
  }
  .mcp__primaries {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 8px;

    .el-button {
      width: 100%;
      margin-left: 0;
    }
  }
  .mcp__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    .el-button {
      width: 100%;
      margin-left: 0;
    }
    .el-dropdown {
      width: 100%;
    }
  }
  .mcp__ddbtn {
    width: 100%;
  }
  .mcp__offline {
    margin-top: 12px;
    font-size: 12px;
    color: var(--vtm-ink-3);
    text-align: center;
  }
  .mcp__vhint {
    font-size: 13px;
    color: var(--vtm-ink-2);
    line-height: 1.5;
  }
</style>
